import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Icon from 'components/common/Icon/Icon'
import Media from 'components/common/Media'
import Mixin from 'components/common/Mixin'
import Theme from 'components/common/Theme/Theme'
import Text from 'components/common/Text/Text'
import Link, { A } from 'components/common/Link/Link'
import Button from 'components/common/Button/Button'
import { formatPhoneNumber } from 'helpers/phone-number'
import { arrayHasValues } from 'helpers/utils'

const SavedLocationOverview = ({
  data: {
    storeNumber,
    address: {
      address1,
      address2,
      city,
      state,
      zipcode
    } = {},
    phoneNumbers,
    storeComingOpenHours,
    storeComingClosedHours,
    storeDetailsUrl,
    tomorrowStoreComingOpenHours,
    tomorrowStoreComingClosedHours,
    distanceRadius
  },
  // MyAccount's StoreLocator props
  isMyAccount,
  routeToLocationUrl,
  showChangeStoreButton,
  handleEditClick,
  handlePhoneLinkClick,
  threeStepper
}) =>
  <React.Fragment>
    <LabelWrapper data-at-savedlocation-checkicon>
      <CustomIcon asset="success" width="20" height="21" />
      <LabelText>My store</LabelText>
    </LabelWrapper>
    <Section>
      <Header data-at-savedlocation-address1>
        { address1 }
        <br />
        { city }, { state } { zipcode } [#{ storeNumber }]
      </Header>
      <Media.Desktop>
        {
          arrayHasValues(phoneNumbers) &&
              phoneNumbers.map((phone, i) =>
                <PhoneNumber data-at-savedlocation-phone key={ i }> { formatPhoneNumber(phone) } </PhoneNumber>)
        }
      </Media.Desktop>
      <Media.Mobile>
        {
          arrayHasValues(phoneNumbers) &&
              phoneNumbers.map((phone, i) =>
                <A key={ i } href={ `tel:+1${phone}` } anchor onClick={ handlePhoneLinkClick }>
                  <PhoneNumber> { formatPhoneNumber(phone) } </PhoneNumber>
                </A>
              )
        }
      </Media.Mobile>
    </Section>
    <Section>
      {
        storeComingOpenHours && storeComingClosedHours &&
          <Line data-at-savedlocation-opentoday> Today&apos;s Hours:
            <Time> { storeComingOpenHours }</Time> - <Time>{ storeComingClosedHours }</Time>
          </Line>
      }
      {
        tomorrowStoreComingOpenHours && tomorrowStoreComingClosedHours &&
          <Line data-at-savedlocation-opentomorrow> Tomorrow&apos;s Hours:
            <Time> { tomorrowStoreComingOpenHours }</Time> - <Time>{ tomorrowStoreComingClosedHours }</Time>
          </Line>
      }
    </Section>
    {
      (!isMyAccount && typeof distanceRadius === 'number') &&
        <DistanceSection>
          <Text size="12">
            <Icon asset="location-red" width="18" height="18" margin="-2px 7px 0 0" alt="" />
            { distanceRadius } miles
          </Text>
        </DistanceSection>
    }
    {
      isMyAccount &&
        <Fragment>
          <LinkWrapper>
            <Link data-at-savedlocation-viewstoredetails to={ storeDetailsUrl }>View Store Details</Link>
            <A data-at-savedlocation-directions href={ routeToLocationUrl }>Directions</A>
          </LinkWrapper>
          <EditButton data-at-savedlocation-edit full="mobile" onClick={ handleEditClick }>EDIT</EditButton>
        </Fragment>
    }
    {
      showChangeStoreButton &&
        <Media.Desktop>
          <ChangeStoreButton
            type="button"
            secondary
            narrow
            threeStepper={ threeStepper }
            onClick={ handleEditClick }>
            CHANGE STORE
          </ChangeStoreButton>
        </Media.Desktop>
    }
  </React.Fragment>

SavedLocationOverview.propTypes = {
  data: PropTypes.shape({
    address: PropTypes.shape({
      address1: PropTypes.string,
      address2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zipcode: PropTypes.string
    }),
    phoneNumbers: PropTypes.arrayOf(PropTypes.string),
    storeComingOpenHours: PropTypes.string,
    storeComingClosedHours: PropTypes.string,
    storeDetailsUrl: PropTypes.string,
    storeNumber: PropTypes.string,
    tomorrowStoreComingOpenHours: PropTypes.string,
    tomorrowStoreComingClosedHours: PropTypes.string,
    distanceRadius: PropTypes.number
  }),
  handleEditClick: PropTypes.func,
  handlePhoneLinkClick: PropTypes.func,
  isMyAccount: PropTypes.bool,
  routeToLocationUrl: PropTypes.string,
  showChangeStoreButton: PropTypes.bool,
  threeStepper: PropTypes.bool
}

export default SavedLocationOverview

const LabelWrapper = styled.div`
  position: relative;
  color: ${Theme.colors.green};
  margin-bottom: 20px;
`

const LabelText = styled.div`
  text-transform: uppercase;
  ${Mixin.font.GothamBold()}
  margin-left: 28px;
`

const Section = styled.div`
  margin-bottom: 18px;
`

const DistanceSection = styled.div`
  margin-bottom: 15px;
`

const Header = styled.h3`
  padding-right: 10px;
  margin: 0 0 15px;
  ${Mixin.font.GothamBold(16, 600, 24)}
  ${Media.mobile`
    font-size: 14px;
    line-height: 19px;
  `}
`

const Line = styled.p`
  padding: 0;
  margin: 0 0 5px;
  ${Mixin.font.GothamBook(13)}
`

const PhoneNumber = styled.h3`
  margin: 0 0 5px;
  ${Mixin.font.GothamBold(14)}
`

const CustomIcon = styled(Icon)`
  position: absolute;
  top: -1px;
  left: 0;
  width: 18px;
  height: 18px;
`

const Time = styled(Text)`
  text-transform: lowercase;
`

const LinkWrapper = styled.div`
  ${Mixin.responsive('margin-bottom', '18px', '0')}

  & ${Link},
  & ${A} {
    position: relative;
    ${Mixin.font.GothamBold(12)}
  }

  & ${Link} + ${A} {
    margin-left: 20px;

    ::before {
      position: absolute;
      top: 3px;
      left: -11px;
      width: 1px;
      height: 12px;
      background: ${Theme.colors.light_grey};
      content: '';
    }
  }
`

const EditButton = styled(Button)`
  ${Mixin.responsive('margin-top', '0', '18px')}
`

const ChangeStoreButton = styled(Button)`
  && {
    margin: 0 0 20px;
    border-width: 2px;
  }
`
